import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from './supabase';
import { joinCompanyByCode } from './db';

function LogoMark({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="10" fill="url(#logoGrad)" />
      <path
        d="M20 6L8 11v10c0 7.18 5.13 13.89 12 15.5C27.87 34.89 33 28.18 33 21V11L20 6z"
        fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1"
      />
      <path d="M14 20.5l4 4 8-8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <defs>
        <linearGradient id="logoGrad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2D7DD2" /><stop offset="1" stopColor="#1A4E8A" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// Password strength calculation
function getPasswordStrength(password) {
  if (!password) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (password.length >= 8)  score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 1) return { score, label: 'Weak',        color: 'var(--red)'   };
  if (score === 2) return { score, label: 'Fair',        color: 'var(--amber)' };
  if (score === 3) return { score, label: 'Good',        color: '#F59E0B'      };
  if (score === 4) return { score, label: 'Strong',      color: 'var(--green)' };
  return            { score, label: 'Very Strong',  color: 'var(--green)' };
}

export default function Login({ onLogin, onBack, initialMode = 'login' }) {
  const [mode, setMode]             = useState(initialMode);
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [company, setCompany]       = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(null);

  const passwordChecks = [
    { label: 'At least 12 characters',          met: password.length >= 12          },
    { label: 'One lowercase letter',            met: /[a-z]/.test(password)         },
    { label: 'One uppercase letter',            met: /[A-Z]/.test(password)         },
    { label: 'One number',                      met: /[0-9]/.test(password)         },
    { label: 'One special character (! @ # $)', met: /[^A-Za-z0-9]/.test(password) },
  ];
  const passwordValid = passwordChecks.every(c => c.met);
  const strength = getPasswordStrength(password);

  const isLocked = lockedUntil && Date.now() < lockedUntil;
  const lockMinsLeft = isLocked ? Math.ceil((lockedUntil - Date.now()) / 60000) : 0;

  // Google OAuth — Authorized origins must include https://theclearaudit.com
  // in Google Cloud Console → Credentials → OAuth Client → Authorized JavaScript origins
  // If getting "origin not allowed" errors, add theclearaudit.com there
  async function handleGoogleSignIn() {
    setLoading(true);
    setError('');
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'https://theclearaudit.com',
        },
      });
      if (oauthError) throw oauthError;
    } catch (err) {
      setError(err.message || 'Google sign in failed. Please try again.');
      setLoading(false);
    }
  }

  async function handleSubmit() {
    setError(''); setSuccess('');

    if (isLocked) {
      setError(`Account temporarily locked. Try again in ${lockMinsLeft} minute${lockMinsLeft !== 1 ? 's' : ''}.`);
      return;
    }

    if (!email) { setError('Email is required.'); return; }
    if (mode !== 'reset' && !password) { setError('Password is required.'); return; }
    if (mode === 'signup') {
      if (!company)      { setError('Please enter your company name.'); return; }
      if (!passwordValid){ setError('Please meet all password requirements below.'); return; }
      if (!agreedToTerms){ setError('You must agree to the Terms of Service and Privacy Policy to create an account.'); return; }
    }

    setLoading(true);

    if (mode === 'login') {
      const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) {
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        if (newAttempts >= 5) {
          setLockedUntil(Date.now() + 15 * 60 * 1000);
          setError('Too many failed attempts. Your account is temporarily locked for 15 minutes.');
        } else {
          const remaining = 5 - newAttempts;
          setError(`Incorrect email or password. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining before temporary lockout.`);
        }
      } else {
        setFailedAttempts(0);
        setLockedUntil(null);
        onLogin();
      }

    } else if (mode === 'signup') {
      const { data: signupData, error: signupError } = await supabase.auth.signUp({
        email, password,
        options: { data: { company_name: company } },
      });
      if (signupError) {
        setError(signupError.message);
      } else {
        // Send welcome email non-blocking
        try {
          const trialEndsAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
            .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
          await supabase.functions.invoke('send-welcome-email', {
            body: { email, companyName: company || email, trialEndsAt },
          });
        } catch (_) { /* non-fatal */ }

        // If a company code was entered, try to join that company
        if (companyCode.trim() && signupData?.user?.id) {
          const result = await joinCompanyByCode(signupData.user.id, companyCode.trim());
          if (result.success) {
            setSuccess(`Account created! You've joined ${result.company?.name || 'the company'}. Check your email to confirm, then sign in.`);
          } else {
            setSuccess(`Account created! However, the company code was invalid: ${result.error} You can still sign in and join a team later.`);
          }
        } else {
          setSuccess('Account created! Check your email to confirm, then sign in.');
        }
        setMode('login');
      }

    } else if (mode === 'reset') {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email);
      if (resetError) {
        if (resetError.status === 429 || resetError.message?.includes('rate limit')) {
          setError('Too many reset requests. Please wait a few minutes and try again.');
        } else {
          setError(resetError.message);
        }
      }
      else setSuccess('Reset link sent — check your inbox.');
    }

    setLoading(false);
  }

  const titles       = { login: 'Welcome back',        signup: 'Create your account',      reset: 'Reset your password' };
  const subtitles    = { login: 'Sign in to continue', signup: 'Start your free 7-day trial', reset: "We'll send you a reset link" };
  const buttonLabels = {
    login:  loading ? 'Signing in…'       : 'Sign In',
    signup: loading ? 'Creating account…' : 'Create Account',
    reset:  loading ? 'Sending…'          : 'Send Reset Link',
  };

  const inputStyle = {
    width: '100%', background: 'var(--surface)', border: '1px solid var(--border2)',
    borderRadius: 7, padding: '10px 13px', fontSize: 13, color: 'var(--text)',
    fontFamily: 'var(--font)', outline: 'none', transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      style={{ height: '100vh', display: 'flex', fontFamily: 'var(--font)', overflow: 'hidden' }}
    >
      {/* ── LEFT PANEL ── */}
      <div className="login-left-panel" style={{
        width: 420, flexShrink: 0,
        background: 'linear-gradient(145deg, #0A0E1A 0%, #0F1930 50%, #0A1428 100%)',
        borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', padding: '48px 40px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.04,
          backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
        <div style={{
          position: 'absolute', top: -80, right: -80, width: 320, height: 320,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(45,125,210,0.18) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 64, position: 'relative' }}>
          <LogoMark size={38} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#E8EDF5', letterSpacing: '-0.01em' }}>TheClearAudit</div>
            <div style={{ fontSize: 10, color: '#5A6478', fontFamily: 'var(--mono)', letterSpacing: '0.08em' }}>COMPLIANCE PLATFORM</div>
          </div>
        </div>
        <div style={{ position: 'relative', flex: 1 }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#E8EDF5', lineHeight: 1.25, marginBottom: 16, letterSpacing: '-0.02em' }}>
            Enterprise compliance<br />
            <span style={{ color: '#2D7DD2' }}>across every framework</span>
          </h2>
          <p style={{ fontSize: 13, color: '#5A6478', lineHeight: 1.7, marginBottom: 40 }}>
            From CMMC to FedRAMP — manage your entire compliance posture from one unified platform built for regulated industries.
          </p>
          {[
            'CMMC 2.0 · SOC 2 · ISO 27001 · HIPAA · FedRAMP',
            'Auto-generate SSP and POA&M with one click',
            'Real-time gap analysis across all 110+ controls',
            'AI-powered compliance assistant and guidance',
          ].map(text => (
            <div key={text} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16,
              padding: '12px 14px',
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
            }}>
              <div style={{
                width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
                background: 'rgba(45,125,210,0.2)', border: '1px solid rgba(45,125,210,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5.5l2.5 2.5 4-5" stroke="#2D7DD2" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span style={{ fontSize: 12, color: '#8A95A8', lineHeight: 1.5 }}>{text}</span>
            </div>
          ))}
        </div>
        <div style={{
          marginTop: 24, padding: '10px 14px',
          background: 'rgba(45,125,210,0.1)', border: '1px solid rgba(45,125,210,0.2)',
          borderRadius: 8, position: 'relative',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#2D7DD2', marginBottom: 2 }}>CMMC Phase 2 — November 2026</div>
          <div style={{ fontSize: 11, color: '#5A6478' }}>Defense contractors must be certified before DoD contract renewal.</div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="login-right-panel" style={{
        flex: 1, overflowY: 'auto', background: 'var(--bg)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px',
      }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 6 }}>
              <LogoMark size={36} />
              <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>TheClearAudit</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>Multi-framework compliance platform</div>
          </div>

          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '32px 28px', animation: 'fadeInScale 0.25s ease both',
          }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                {titles[mode]}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>{subtitles[mode]}</div>
            </div>

            {/* Company Name (signup only) */}
            {mode === 'signup' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Company Name
                </label>
                <input
                  type="text" placeholder="e.g. Acme Defense Co."
                  value={company} onChange={e => setCompany(e.target.value)}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                />
              </div>
            )}

            {/* Company Code (signup only — optional) */}
            {mode === 'signup' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Company Code <span style={{ color: 'var(--text3)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(Optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter code if joining an existing company"
                  value={companyCode}
                  onChange={e => setCompanyCode(e.target.value.toUpperCase())}
                  style={{ ...inputStyle, fontFamily: 'var(--mono)', letterSpacing: '0.06em' }}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5, lineHeight: 1.5 }}>
                  If your employer gave you a company code, enter it here to join their account.
                </div>
              </div>
            )}

            {/* Google Sign In */}
            {mode !== 'reset' && (
              <>
                <button
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  style={{
                    width: '100%', minHeight: 44, padding: '10px', borderRadius: 8,
                    fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                    border: '1px solid #E5E7EB', background: '#FFFFFF',
                    color: '#1A1A2E', fontFamily: 'var(--font)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    opacity: loading ? 0.7 : 1,
                    transition: 'background 0.15s, border-color 0.15s',
                    marginBottom: 0,
                  }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = '#F9FAFB'; e.currentTarget.style.borderColor = '#D1D5DB'; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border2)' }} />
                  <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>or continue with email</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border2)' }} />
                </div>
              </>
            )}

            {/* Email */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Email Address
              </label>
              <input
                type="email" placeholder="you@company.com"
                value={email} onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border2)'}
              />
            </div>

            {/* Password with show/hide toggle */}
            {mode !== 'reset' && (
              <div style={{ marginBottom: mode === 'signup' ? 8 : 24 }}>
                <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    style={{ ...inputStyle, paddingRight: 52 }}
                    onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    style={{
                      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 11, fontWeight: 500, fontFamily: 'var(--font)',
                      color: 'var(--text3)', padding: '2px 0', lineHeight: 1,
                      letterSpacing: '0.01em',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>

                {/* Password strength bar (signup only) */}
                {mode === 'signup' && password.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                      {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} style={{
                          flex: 1, height: 3, borderRadius: 2,
                          background: i <= strength.score ? strength.color : 'var(--border2)',
                          transition: 'background 0.2s',
                        }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: strength.color, fontWeight: 500 }}>
                      {strength.label}
                    </div>
                  </div>
                )}

                {/* Password requirements (signup only) */}
                {mode === 'signup' && (
                  <div style={{
                    marginTop: 10, padding: 12,
                    background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)',
                  }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontWeight: 500 }}>
                      Password requirements
                    </div>
                    {passwordChecks.map(req => (
                      <div key={req.label} style={{
                        display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, marginBottom: 5,
                        color: req.met ? 'var(--green)' : 'var(--text3)', transition: 'color 0.2s',
                      }}>
                        <div style={{
                          width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 9, fontWeight: 700, transition: 'all 0.2s',
                          background: req.met ? 'var(--green-bg)' : 'var(--surface2)',
                          color: req.met ? 'var(--green)' : 'var(--text3)',
                          border: `1px solid ${req.met ? 'var(--green)' : 'var(--border2)'}`,
                        }}>
                          {req.met ? '✓' : ''}
                        </div>
                        {req.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Terms of Service checkbox (signup only) */}
            {mode === 'signup' && (
              <div style={{ marginBottom: 20, marginTop: 12 }}>
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={e => setAgreedToTerms(e.target.checked)}
                    style={{ marginTop: 2, accentColor: 'var(--accent)', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>
                    I agree to the{' '}
                    <span
                      onClick={() => window.open('/terms', '_blank')}
                      style={{ color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}
                    >Terms of Service</span>
                    {' '}and{' '}
                    <span
                      onClick={() => window.open('/privacy', '_blank')}
                      style={{ color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}
                    >Privacy Policy</span>
                  </span>
                </label>
              </div>
            )}

            {/* Lock warning */}
            {isLocked && (
              <div style={{
                background: 'var(--amber-bg)', border: '1px solid var(--amber)',
                borderRadius: 6, padding: '10px 12px', fontSize: 12, color: 'var(--amber)',
                marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span>
                  Account temporarily locked due to multiple failed attempts.
                  Try again in <strong>{lockMinsLeft} minute{lockMinsLeft !== 1 ? 's' : ''}</strong>.
                </span>
              </div>
            )}

            {/* Error */}
            {error && (
              <div style={{
                background: 'var(--red-bg)', border: '1px solid var(--red)',
                borderRadius: 6, padding: '10px 12px', fontSize: 12,
                color: 'var(--red)', marginBottom: 16, animation: 'fadeIn 0.2s ease both',
              }}>{error}</div>
            )}

            {/* Success */}
            {success && (
              <div style={{
                background: 'var(--green-bg)', border: '1px solid var(--green)',
                borderRadius: 6, padding: '10px 12px', fontSize: 12,
                color: 'var(--green)', marginBottom: 16, animation: 'fadeIn 0.2s ease both',
              }}>{success}</div>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={loading || isLocked}
              style={{
                width: '100%', padding: '11px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                cursor: loading || isLocked ? 'not-allowed' : 'pointer',
                border: '1px solid var(--accent)', background: 'var(--accent)',
                color: '#fff', fontFamily: 'var(--font)',
                opacity: loading || isLocked ? 0.6 : 1,
              }}
              onMouseEnter={e => !loading && !isLocked && (e.currentTarget.style.background = '#2570C0')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--accent)')}
            >
              {buttonLabels[mode]}
            </button>


            {/* Mode switcher */}
            <div style={{ marginTop: 20, textAlign: 'center' }}>
              {mode === 'login' && (
                <>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>Don't have an account? </span>
                  <span onClick={() => { setMode('signup'); setError(''); setSuccess(''); }}
                    style={{ fontSize: 12, color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}>
                    Sign up free
                  </span>
                  <div style={{ marginTop: 10 }}>
                    <span onClick={() => { setMode('reset'); setError(''); setSuccess(''); }}
                      style={{ fontSize: 12, color: 'var(--text3)', cursor: 'pointer' }}>
                      Forgot password?
                    </span>
                  </div>
                </>
              )}
              {mode === 'signup' && (
                <>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>Already have an account? </span>
                  <span onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                    style={{ fontSize: 12, color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}>
                    Sign in
                  </span>
                </>
              )}
              {mode === 'reset' && (
                <span onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                  style={{ fontSize: 12, color: 'var(--text3)', cursor: 'pointer' }}>
                  ← Back to sign in
                </span>
              )}
            </div>
          </div>

          {/* Back + Legal */}
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            {onBack && (
              <span onClick={onBack} style={{ fontSize: 12, color: 'var(--text3)', cursor: 'pointer', display: 'block', marginBottom: 10 }}>
                ← Back to home
              </span>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
              <span onClick={() => window.open('/terms', '_blank')}
                style={{ fontSize: 11, color: 'var(--text3)', cursor: 'pointer', textDecoration: 'underline' }}>Terms</span>
              <span onClick={() => window.open('/privacy', '_blank')}
                style={{ fontSize: 11, color: 'var(--text3)', cursor: 'pointer', textDecoration: 'underline' }}>Privacy</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
